import {
  DevelopmentEarnestMoneySummary,
  IDevelopmentEarnestMoneySummary,
} from '@app/models/DevelopmentEarnestMoneySummary'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IEarnestMoneyListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<IDevelopmentEarnestMoneySummary>
  readonly totalItems: number
}

const EarnestMoneyListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<DevelopmentEarnestMoneySummary>(),
  totalItems: 0,
})

export class EarnestMoneyListState extends EarnestMoneyListStateRecord
  implements IEarnestMoneyListState {
  public static fromJS(initialState?: Partial<IEarnestMoneyListState>) {
    if (!initialState) {
      return new EarnestMoneyListState()
    }

    const { items, ...rest } = initialState
    return new EarnestMoneyListState({
      items: items
        ? List(items.map((i) => DevelopmentEarnestMoneySummary.fromJS(i)))
        : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<DevelopmentEarnestMoneySummary>
  public readonly totalItems: number

  constructor(props?: Partial<EarnestMoneyListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum EarnestMoneyListActionTypes {
  LoadEarnestMoney = 'EARNEST_MONEY_LIST::LOAD_EARNEST_MONEY',
  LoadEarnestMoneyFailure = 'EARNEST_MONEY_LIST::LOAD_EARNEST_MONEY_FAILURE',
  LoadEarnestMoneyRequest = 'EARNEST_MONEY_LIST::LOAD_EARNEST_MONEY_REQUEST',
  LoadEarnestMoneySuccess = 'EARNEST_MONEY_LIST::LOAD_EARNEST_MONEY_SUCCESS',
  SetAssignedTo = 'EARNEST_MONEY_LIST::SET_ASSIGNED_TO',
  SetCity = 'EARNEST_MONEY_LIST::SET_CITY',
  SetClient = 'EARNEST_MONEY_LIST::SET_CLIENT',
  SetDueDate = 'EARNEST_MONEY_LIST::SET_DUE_DATE',
  SetFilterText = 'EARNEST_MONEY_LIST::SET_FILTER_TEXT',
  SetScope = 'EARNEST_MONEY_LIST::SET_SCOPE',
  SetStatus = 'EARNEST_MONEY_LIST::SET_STATUS',
  SetSortOrder = 'EARNEST_MONEY_LIST::SET_SORT_ORDER',
}

interface ILoadEarnestMoneyAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: EarnestMoneyListActionTypes.LoadEarnestMoney
}

interface ILoadEarnestMoneyFailureAction {
  error: string
  type: EarnestMoneyListActionTypes.LoadEarnestMoneyFailure
}

interface ILoadEarnestMoneyRequestAction {
  type: EarnestMoneyListActionTypes.LoadEarnestMoneyRequest
}

interface ILoadEarnestMoneySuccessAction {
  append: boolean
  items: DevelopmentEarnestMoneySummary[]
  totalItems: number
  type: EarnestMoneyListActionTypes.LoadEarnestMoneySuccess
}

interface ISetFilterTextAction {
  filter: string
  type: EarnestMoneyListActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadEarnestMoneyFailureAction
  | ILoadEarnestMoneyRequestAction
  | ILoadEarnestMoneySuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadEarnestMoney(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadEarnestMoneyAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: EarnestMoneyListActionTypes.LoadEarnestMoney,
  }
}

function loadEarnestMoneyFailure(
  error: string,
): ILoadEarnestMoneyFailureAction {
  return {
    error,
    type: EarnestMoneyListActionTypes.LoadEarnestMoneyFailure,
  }
}

function loadEarnestMoneyRequest(): ILoadEarnestMoneyRequestAction {
  return {
    type: EarnestMoneyListActionTypes.LoadEarnestMoneyRequest,
  }
}

function loadEarnestMoneySuccess(
  items: DevelopmentEarnestMoneySummary[],
  append: boolean,
  totalItems: number,
): ILoadEarnestMoneySuccessAction {
  return {
    append,
    items,
    totalItems,
    type: EarnestMoneyListActionTypes.LoadEarnestMoneySuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: EarnestMoneyListActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadEarnestMoney,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<EarnestMoneyListState> = (
  state: EarnestMoneyListState = new EarnestMoneyListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case EarnestMoneyListActionTypes.LoadEarnestMoneyFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as EarnestMoneyListState

    case EarnestMoneyListActionTypes.LoadEarnestMoneyRequest:
      return state.set('isLoading', true) as EarnestMoneyListState

    case EarnestMoneyListActionTypes.LoadEarnestMoneySuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<
            DevelopmentEarnestMoneySummary
          >)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as EarnestMoneyListState

    case EarnestMoneyListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as EarnestMoneyListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadEarnestMoneySaga(action: ILoadEarnestMoneyAction) {
  try {
    yield put(loadEarnestMoneyRequest())

    const currentState: EarnestMoneyListState = yield select(
      (state: ApplicationState) => state.earnestMoneyList,
    )

    const query: string[] = []

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/dueDiligenceItems/earnestMoneySummary'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: DevelopmentEarnestMoneySummary[] = result.results.map(
        (c: any) => DevelopmentEarnestMoneySummary.fromJS(c),
      )
      yield put(loadEarnestMoneySuccess(contacts, false, result.count))
    } else {
      yield put(loadEarnestMoneyFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadEarnestMoneyFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadEarnestMoney() {
  yield takeLatest(
    EarnestMoneyListActionTypes.LoadEarnestMoney,
    loadEarnestMoneySaga,
  )
}

export function* earnestMoneyListRootSaga() {
  yield fork(watchLoadEarnestMoney)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): EarnestMoneyListState => state.earnestMoneyList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
